import * as PlayerService from "@/services/media/player.service";
import PipService from '@/services/media/pip.service';
import mime from 'mime';

export const media = {
    namespaced: true,
    state: {
        mediaFile: null,
        playlist: [],
        playlistIndex: 0,
        repeatMode: 0,
        playerOpen: false,
        pipOpened: false,
        videoWindow: null,
    },
    actions: {
        async play({ dispatch, commit }, payload) {
            const {
                files,
                shuffle = false
            } =
                Array.isArray(payload)
                    ? {
                        files: payload,
                        shuffle: false
                    }
                    : payload;

            let playlist = PlayerService.createPlaylist(files);
            if (!playlist.length)
                return false;
                
            if (shuffle) {
                playlist = PlayerService.shufflePlaylist(playlist);
            }

            commit("setPlaylist", playlist);
            commit("setPlaylistIndex", 0);

            return await dispatch("playIndex", 0);
        },
        async playFile({ commit, state }, payload) {
            const item = payload?.item ?? payload;
            if (!item)
                return false;

            const file = {
                id: item.id,
                path: item.path,
                name: item.file?.name ?? item.name,
                isFile: item.isFile,
                meta: { ...item.meta }
            };
            let media;

            try {
                media = await PlayerService.playFile(file);
            }
            catch (error) {
                if (error.response?.status === 404) {
                    const err = new Error("File not found");
                    err.code = "FILE_NOT_FOUND";

                    throw err;
                }

                throw error;
            }

            if (!media)
                return false;

            let index = state.playlist.findIndex(playlistItem =>
                    playlistItem.path === media.path && playlistItem.name === media.name);

            if (index === -1) {
                commit("setPlaylist", [file]);
                index = 0;
            }

            commit("setPlaylistIndex", index);
            commit("setMediaFile", media);
            commit("setPlayerOpen", true);

            return true;
        },
        async playIndex({ dispatch, commit, state }, index) {
            while (index >= 0 && index < state.playlist.length) {
                const item = state.playlist[index];
                if (!item)
                    return false;

                try {
                    const result = await dispatch("playFile", item);
                    if (result) {
                        return true;
                    }

                    return false;
                }
                catch (error) {
                    if (error.code !== "FILE_NOT_FOUND") {
                        throw error;
                    }

                    const newPlaylist = state.playlist.filter((_, i) => i !== index);

                    commit("setPlaylist", newPlaylist);
                }
            }

            return false;
        },
        async playPrev({ dispatch, state }) {
            const total = state.playlist.length;
            if (!total)
                return;

            const prevIndex = state.playlistIndex <= 0 ? total - 1 : state.playlistIndex - 1;
            await dispatch("playIndex", prevIndex);
        },
        async playNext({ dispatch, state }) {
            const total = state.playlist.length;
            if (!total)
                return;

            const nextIndex = state.playlistIndex >= total - 1 ? 0 : state.playlistIndex + 1;
            await dispatch("playIndex", nextIndex);
        },
        setPlaylist({ commit }, playlist) {
            commit("setPlaylist", playlist);
        },
        shufflePlaylist({ state, commit }) {
            const current = state.mediaFile?.name;
            const currentItem = state.playlist.find(f => f.name === current);
            const others = state.playlist.filter(f => f.name !== current);

            for (let i = others.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [others[i], others[j]] = [others[j], others[i]];
            }

            commit("setPlaylist", currentItem
                    ? [currentItem, ...others]
                    : others
            );
        },
        setRepeatMode({ commit }, mode) {
            commit("setRepeatMode", mode);
        },
        closePlayer({ commit }) {
            commit("setPlayerOpen", false);
            commit("setMediaFile", null);
            commit("setPlaylist", []);
            commit("setPlaylistIndex", 0);
        },
        async removePlaylist({ state, commit, dispatch }, index) {
            const removed = state.playlist[index];
            commit("removePlaylist", index);

            if (!removed)
                return;

            if (removed.name !== state.mediaFile?.name)
                return;

            if (index < state.playlist.length) {
                await dispatch("playIndex", index);
                return;
            }
            
            if (state.playlist.length) {
                await dispatch("playIndex", state.playlist.length - 1);
                return;
            }

            dispatch("closePlayer");
        },
        async openPip({ commit }, { videoEl, previewCanvas, width, height, feedback, onPipOpen, onPipClose }) {
            PipService.onClose = () => {
                commit("setPipOpened", false);
            };

            try {
                await PipService.open(videoEl, previewCanvas, width, height, feedback, onPipOpen, onPipClose);
                commit("setPipOpened", true);
            } catch (error) {
                console.error("Document PiP open failed:", error);
                commit("setPipOpened", false);
            }
        },
        async closePip({ commit }) {
            await PipService.close();
            commit("setPipOpened", false);
        },
        async addSelectedToPlaylist({ state, commit }, { files, path }) {
            if (!files?.length) {
                return;
            }

            const additions = [];

            for (const file of files) {
                const type = mime.getType(file.name)?.toLowerCase() || "";
                if (!type.includes("video") && !type.includes("audio")) {
                    continue;
                }

                const exists = state.playlist.some(p => p.name === file.name && p.path === path);
                if (exists) {
                    continue;
                }

                additions.push({
                    ...file,
                    path,
                    urlToken: "",
                });
            }

            if (additions.length) {
                commit("appendPlaylist", additions);
            }
        },
    },
    mutations: {
        setMediaFile(state, media) {
            state.mediaFile = media;
        },
        setPlaylist(state, playlist) {
            state.playlist = playlist;
        },
        setPlaylistIndex(state, index) {
            state.playlistIndex = index;
        },
        setPlayerOpen(state, open) {
            state.playerOpen = open;
        },
        removePlaylist(state, index) {
            state.playlist.splice(index, 1);
        },
        setRepeatMode(state, mode) {
            state.repeatMode = mode;
        },
        setPipOpened(state, value) {
            state.pipOpened = value;
        },
        setVideoWindowPosition(state, rect) {
            state.videoWindow = {
                ...state.videoWindow,
                ...rect,
            };
        },
        appendPlaylist(state, files) {
            state.playlist.push(...files);
        },
        setFavoriteGroupId(state, id) {
            state.favoriteGroupId = id;
        }
    }
}